// ── The unsaved registry: Back, in-app navigation and a parent unmount ask before typing is lost ──
// (owner 2026-10-20: 「需要拦」)
//
// Both document shells already ask before they CLOSE with something typed (CompanyWorkspace/dialog.tsx
// and MerchantDoc/shell.tsx: X, Esc, backdrop, Cancel). Three other ways of losing the same typing
// asked nothing: the browser's Back (and Forward, the mouse's side button, a trackpad swipe), an
// in-app jump (the workspace menu, Sign out, Back to portal), and anything that UNMOUNTS the window
// from above it. `beforeunload` covers none of them — a single-page app never unloads.
//
// ⛔ WHY NOT react-router's useBlocker. It works only under a data router (createBrowserRouter +
// RouterProvider) and throws under the declarative <BrowserRouter> App.tsx uses. Moving sixty-odd
// routes to a data router for this is not on the table (owner's brief). So:
//
//   · REGISTRY. A window with something to lose registers here (useUnsavedGuard) and deregisters when
//     it is clean, closed or unmounted. Each shell calls it from the ONE place its `dirty` lives, so
//     no form can be forgotten — the shells derive or receive `dirty`, the registry only listens.
//
//   · SENTINEL. While the registry is non-empty, one extra history entry with the SAME address sits on
//     top of the page's own. Back steps onto the page's own entry — the address does not change, so
//     nothing is unmounted — and the popstate handler asks the shells' own question (aw_dlg_discard).
//     Keep editing → the sentinel goes back on top, the operator never moved. Discard → one more step
//     back, which is the Back they pressed. When the registry empties the sentinel is stepped off
//     again, so a later Back is never swallowed by a leftover entry (the new bug this could make).
//
//   · ONE QUESTION AT EVERY OTHER DOOR. guardLeave(go) is what an in-app navigation calls instead of
//     navigating: nothing registered → go at once; otherwise the same question, asked once, and on
//     "discard" the sentinel is stepped off FIRST and only then does the navigation run — a page
//     pushed on top of a sentinel would leave a dead Back behind it.
//
// ⚠️ What the History API cannot do is REMOVE an entry. A stepped-off sentinel stays behind as a
// FORWARD entry (history.length still counts it) until the next navigation replaces it. Back never
// reaches it, and Forward onto it is stepped back over (onPop), so no press is ever a dead one.
// ⛔ Chrome's history intervention marks entries the page ADDS without a user activation as
// skippable for its Back button (measured in a real Chromium 152, break-it review 2026-10-20: one
// pushState after a "keep editing" answer left the browser's Back with "no back history" until the
// next click). So the sentinel is only ever pushed when typing arms the page; "keep editing" steps
// FORWARD onto the sentinel it just left (stay()), a traversal. A Back that does land several entries
// down (the long-press menu) is handled as several steps at once — asked the same way, and "keep
// editing" goes forward again by exactly as many.
//
// ⛔ THE TAB'S FIRST ENTRY IS NEVER ARMED (owner 2026-10-20, receipts round 2: 「按建议」). Modules open
// in a new tab, so the workspace is often the first entry of its tab — react-router's `idx` 0 — and
// there Back is disabled because there is nothing to go back to. Pushing a sentinel there turned the
// Back button ON for no reason, and its "Discard" did nothing at all (history.back() from the first
// entry has nowhere to go): the operator confirmed a discard that never happened, and X then asked
// the same question again (guard review, measured 2026-10-20). So on idx 0 nothing is pushed: the
// page is armed in memory only (`armed.id` FIRST), every in-app door still asks through guardLeave,
// the X still asks, a reload or a tab close still meets beforeunload, and a Back that leaves for
// another site is the browser's own "Leave site?". A Forward off the first entry (it can have
// entries ahead of it) is asked like any other step and, on "keep editing", stepped back over.
//
// ⛔ NOT a draft store. Nothing here is written to localStorage or anywhere else (the localStorage
// quota incident): the registry holds tokens in memory and the sentinel holds one integer.
//
// ⛔ The state lives on `window`, not in this module. Two copies of the module (a test loading it
// through two loaders, a hot reload) would otherwise keep two registries and two listeners, and the
// listener that answers Back would be the one that cannot see the window registered with the other.
import { useEffect, useRef } from 'react';
import i18next from 'i18next';

type Entry = { busy: boolean };
type Armed = { id: number; url: string; state: any };
// `pass`: the router must see where the step lands (stepping over a stale sentinel while the router is
// showing some other page); otherwise the step is between two entries of the page on screen.
type Pending = { kind: 'drop'; then?: () => void; pass?: boolean } | { kind: 'return' } | null;
type State = {
  entries: Set<Entry>;
  seq: number;
  armed: Armed | null;          // the live sentinel and the page entry under it
  dropped: Set<number>;         // sentinels this page stepped back off (now forward entries)
  pending: Pending;             // a history step of OUR OWN in flight, whose popstate is ours to swallow
  pendingTimer: any;
  passNext: boolean;            // the next popstate is a leave the operator confirmed: the router takes it
  released: boolean;            // "discard" was answered: nothing asks or re-arms until the windows are gone
  releaseTimer: any;
  scheduled: boolean;
};

/** The history-state key the sentinel carries. react-router keeps its own `usr`/`key`/`idx` beside it. */
export const SENTINEL_KEY = '__awUnsaved';
/** `armed.id` of a page armed WITHOUT a sentinel: the tab's first entry (react-router idx 0). Sentinel
 *  ids start at 1, so it can never be mistaken for one. */
const FIRST = 0;
const PENDING_MS = 1000;   // a step of ours whose popstate never came (another push cancelled it)
const RELEASE_MS = 1500;   // a confirmed leave that unmounted nothing: the window is guarded again

const hasWindow = (): boolean => typeof window !== 'undefined' && !!window.history && typeof window.addEventListener === 'function';
const S = (): State => {
  const w = window as any;
  if (!w.__awUnsavedState) {
    w.__awUnsavedState = {
      entries: new Set<Entry>(), seq: 0, armed: null, dropped: new Set<number>(), pending: null, pendingTimer: null,
      passNext: false, released: false, releaseTimer: null, scheduled: false,
    } as State;
  }
  return w.__awUnsavedState as State;
};
const markOf = (st: any): number => (st && typeof st === 'object' && typeof st[SENTINEL_KEY] === 'number' ? st[SENTINEL_KEY] : 0);
const unmark = (st: any): any => {
  if (!st || typeof st !== 'object') return st;
  const o = { ...st };
  delete o[SENTINEL_KEY];
  return o;
};
const anyBusy = (): boolean => { let b = false; S().entries.forEach(e => { if (e.busy) b = true; }); return b; };

/** Is anything registered? Only a window with something typed is; its save being in flight then
 *  makes Back and the doors wait (anyBusy), but a CLEAN window's save registers nothing. */
export function hasUnsaved(): boolean { return hasWindow() && S().entries.size > 0; }

/** THE question, worded once for every door the registry guards: the shells' own aw_dlg_discard. */
export function askDiscard(): boolean { return window.confirm(i18next.t('aw_dlg_discard')); }

function schedule(): void {
  const s = S();
  if (s.scheduled) return;
  s.scheduled = true;
  // Coalesced: a window that deregisters and another that registers in the same commit (a pane
  // re-keyed under them) must not push and pop the sentinel in between.
  Promise.resolve().then(sync);
}

function startPending(p: Pending): void {
  const s = S();
  s.pending = p;
  clearTimeout(s.pendingTimer);
  s.pendingTimer = setTimeout(() => {
    const cur = S();
    if (cur.pending !== p) return;
    cur.pending = null;
    if (p && p.kind === 'drop' && p.then) p.then();
    schedule();
  }, PENDING_MS);
}

function release(): void {
  const s = S();
  s.released = true;
  clearTimeout(s.releaseTimer);
  // A leave that unmounted nothing (or a Back with no page behind it): after a moment the window is
  // guarded again, and a passNext nobody consumed must not wave the NEXT Back through unasked.
  s.releaseTimer = setTimeout(() => { const c = S(); c.released = false; c.passNext = false; schedule(); }, RELEASE_MS);
}

/** Step back off the sentinel this page is on. `then` runs once the browser has actually arrived. */
function drop(then?: () => void): void {
  const s = S();
  const id = markOf(window.history.state);
  if (id) s.dropped.add(id);
  s.armed = null;
  startPending({ kind: 'drop', then });
  window.history.back();
}

/** Bring history in line with the registry: a sentinel while something is registered, none after. */
function sync(): void {
  const s = S();
  s.scheduled = false;
  if (s.released && s.entries.size === 0) { s.released = false; clearTimeout(s.releaseTimer); }
  if (s.pending || s.released) return;
  const want = s.entries.size > 0;
  const st = window.history.state;
  const on = markOf(st);
  if (!want && s.armed && s.armed.id === FIRST) s.armed = null;   // nothing was pushed, so nothing to step off
  if (want && !on && idxOf(st) === 0) {
    // The tab's first entry: armed in memory, nothing pushed (see the header).
    if (!s.armed || s.armed.id !== FIRST || s.armed.url !== window.location.href) s.armed = { id: FIRST, url: window.location.href, state: st };
    return;
  }
  if (want && !on) {
    const id = ++s.seq;
    s.armed = { id, url: window.location.href, state: st };
    window.history.pushState({ ...(st && typeof st === 'object' ? st : {}), [SENTINEL_KEY]: id }, '', window.location.href);
  } else if (want && on && (!s.armed || s.armed.id !== on)) {
    // On a sentinel this registry did not push (one left by a reload of this very page): adopt it.
    s.armed = { id: on, url: window.location.href, state: unmark(st) };
  } else if (!want && on) {
    drop();
  }
}

const sameEntry = (st: any, a: Armed): boolean =>
  !markOf(st) && window.location.href === a.url && String(st?.key ?? '') === String(a.state?.key ?? '');
const idxOf = (st: any): number | null => (st && typeof st.idx === 'number' ? st.idx : null);
const noHash = (u: string): string => String(u || '').split('#')[0];

/** Keep editing: undo the step the browser just took, so the operator is where they were. */
function stay(st: any): void {
  const s = S();
  const a = s.armed;
  if (!a) return;
  if (a.id === FIRST) {
    // No sentinel to return to: the browser stepped off the first entry itself (Forward — it has no
    // Back). Undo exactly the steps it took; somewhere unknown → put the page's address back on top.
    if (sameEntry(st, a)) return;
    const from = idxOf(a.state), to = idxOf(st);
    if (from !== null && to !== null && from !== to) { startPending({ kind: 'return' }); window.history.go(from - to); return; }
    window.history.pushState(a.state, '', a.url);
    return;
  }
  if (sameEntry(st, a)) {
    // One step: the page's own entry, and the sentinel just stepped off is the entry right ahead of
    // it. Step FORWARD onto it again — a traversal, not a new entry. ⛔ NOT pushState (break-it review
    // 2026-10-20, measured in a real Chromium 152): an entry added with no user activation since the
    // last one (the operator only answered a confirm) trips Chrome's history-manipulation
    // intervention — the page's entries are marked skippable and the browser's Back went DEAD ("no
    // back history") until the next click or keypress in the page. If this step never lands (no
    // entry ahead after all), the pending timer re-syncs, which pushes a fresh sentinel as before.
    startPending({ kind: 'return' });
    window.history.go(1);
    return;
  }
  const from = idxOf(a.state), to = idxOf(st);
  if (from !== null && to !== null && from >= to) {
    // Several steps at once (the long-press menu on Back): go forward again by exactly as many.
    startPending({ kind: 'return' });
    window.history.go(from - to + 1);
    return;
  }
  // Nowhere known to return to: put the page's address and its sentinel back on top of where the
  // browser landed. The router never saw the step, so the screen and the address agree again.
  window.history.pushState(a.state, '', a.url);
  window.history.pushState({ ...(a.state && typeof a.state === 'object' ? a.state : {}), [SENTINEL_KEY]: a.id }, '', a.url);
}

function onPop(e: PopStateEvent): void {
  const s = S();
  const st = window.history.state;
  if (s.pending) {
    // A step of our own (stepping off, or returning to, the sentinel): same address, so the router
    // has nothing to learn from it and must not re-render on it.
    const p = s.pending;
    s.pending = null;
    clearTimeout(s.pendingTimer);
    if (!(p.kind === 'drop' && p.pass)) e.stopImmediatePropagation();
    if (p.kind === 'drop' && p.then) p.then();
    schedule();
    return;
  }
  if (s.passNext) { s.passNext = false; return; }   // the confirmed leave: the router takes it from here
  if (s.released) return;
  const mark = markOf(st);
  // ⛔ The live sentinel is the one that copies the ARMED page entry — its id AND its router key. The
  // id alone is not enough: ids restart at 1 on every page load, so after a reload a leftover sentinel
  // further back can carry the very id now armed (break-it review 2026-10-20: a long-press Back onto
  // it was waved through to the router and the dirty window unmounted, nothing asked).
  if (mark && s.entries.size && s.armed && s.armed.id === mark && String(st?.key ?? '') === String(s.armed.state?.key ?? '')) return;
  if (mark && !s.entries.size) {
    // A sentinel that is no longer live, reached by Back or Forward with nothing to lose. It is a copy
    // of the page entry under it, so it is stepped over rather than shown as a Back that did nothing.
    // One this page never stepped off itself (an unguarded push left it behind) loses its mark, so it
    // is stepped over once and is an ordinary entry after.
    e.stopImmediatePropagation();
    if (!s.dropped.has(mark)) window.history.replaceState(unmark(st), '', window.location.href);
    startPending({ kind: 'drop', pass: true });
    window.history.back();
    return;
  }
  // ⛔ A LEFTOVER sentinel reached while something IS typed (a long-press Back several entries down,
  // across a push nobody guarded) falls through to the question like any other step off the live one:
  // stepping over it passed the jump to the router and unmounted the window unasked (break-it review
  // 2026-10-20). Keep editing → stay() goes forward again by the idx distance; discard → the router
  // takes the jump, and the emptied registry then steps off the leftover (sync → drop).
  if (!s.entries.size) return;   // nothing to lose: Back is the browser's
  if (st == null && s.armed && noHash(window.location.href) === noHash(s.armed.url)) return;   // an in-page #anchor, not a step back
  // The operator stepped off the sentinel with something to lose. The router does not see this pop
  // unless they confirm, so nothing re-renders and nothing is unmounted while the question is up.
  e.stopImmediatePropagation();
  if (anyBusy() || !askDiscard()) { stay(st); return; }   // a save in flight: as the shells' own close does, nothing
  release();
  const a = s.armed;
  if (a && a.id !== FIRST) s.dropped.add(a.id);
  s.armed = null;
  s.passNext = true;
  if (a && sameEntry(st, a)) window.history.back();   // the Back they pressed, now for real
  else window.dispatchEvent(new (window as any).PopStateEvent('popstate', { state: st }));   // already there: let the router catch up
}

function onBeforeUnload(e: BeforeUnloadEvent): void {
  const s = S();
  if (!s.entries.size || s.released) return;
  // A reload, a closed tab or a full-page jump (window.location.assign from an overlay above the
  // window): the browser's own "Leave site?" — a page cannot put its own words in that box.
  e.preventDefault();
  e.returnValue = '';
}

/** Once per window. Called when this module is first evaluated — which is before <BrowserRouter>
 *  mounts and adds its own popstate listener (App.tsx imports PinnedSession, which imports this) — so
 *  this listener runs first, and in the capture phase as well, and can keep the router from seeing a
 *  step the operator has not confirmed. Called again on first use, for a host that had no window yet. */
function install(): void {
  if (!hasWindow()) return;
  const w = window as any;
  if (w.__awUnsavedInstalled) return;
  w.__awUnsavedInstalled = true;
  window.addEventListener('popstate', onPop, true);
  window.addEventListener('beforeunload', onBeforeUnload);
  // A reload on a sentinel with nothing registered steps off it, as an emptied registry would.
  schedule();
}
install();

/** In-app navigation: ask before `go` when something is registered, never otherwise. Returns false
 *  when the operator chose to keep editing (or a save is in flight), true once `go` has run or is
 *  about to run (after the sentinel is stepped off). */
export function guardLeave(go: () => void): boolean {
  if (!hasWindow()) { go(); return true; }
  const s = S();
  if (!s.entries.size || s.released) { go(); return true; }
  if (anyBusy()) return false;
  if (!askDiscard()) return false;
  release();
  if (s.pending && s.pending.kind === 'drop') {
    const prev = s.pending.then;
    s.pending.then = () => { if (prev) prev(); go(); };
  } else if (!s.pending && markOf(window.history.state)) {
    drop(go);
  } else {
    go();
  }
  return true;
}

/** A shell's registration: while `dirty` and mounted, the page is guarded. Called once per window,
 *  from the place its own `dirty` is decided — never from a form.
 *  ⛔ `busy` alone does NOT register (review 2026-10-20). A clean window that is saving has nothing
 *  to lose, and registering it pushed a sentinel for the length of the request: Back pressed then
 *  did nothing at all, and the stepped-off sentinel stayed behind as one more history entry — both
 *  on a window with nothing typed, which the brief requires to leave history exactly as it was.
 *  A DIRTY window's save in flight still holds Back and every door (anyBusy reads `busy` live). */
export function useUnsavedGuard(dirty: boolean, busy = false): void {
  const mine = useRef<Entry>({ busy: false });
  mine.current.busy = !!busy;   // read at the moment of asking, never stale
  const on = !!dirty;
  useEffect(() => {
    if (!on || !hasWindow()) return;
    install();
    const e = mine.current;
    S().entries.add(e);
    schedule();
    return () => { S().entries.delete(e); schedule(); };
  }, [on]);
}
